<script setup lang="ts">
import { computed } from 'vue';

import { BadgeEmits, BadgeProps } from 'lib/components/badge/Badge.vue.d';
import Button from 'lib/components/button/Button.vue';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';
import { formatUserName } from 'lib/utils/textFormatter.util';

const props = defineProps<BadgeProps>();
const emit = defineEmits<BadgeEmits>();

const { defaultProps } = useWangsVueConfig('Badge');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const hasValue = computed(() => {
  const type = typeof props.label;
  return (
    type === 'number' ||
    (type === 'string' && props.label && props.label !== '-')
  );
});

const badgeSeverity = computed<BadgeProps['severity']>(() => {
  return props.severity || config.value.getSeverity?.(props.label);
});

const formattedText = computed(() => {
  if (!hasValue.value) return '-';

  switch (props.format) {
    case 'nowrap':
      return props.label;
    case 'username':
      return formatUserName(props.label);
    default:
      if (props.label.length > 12) {
        const firstWord = props.label.slice(0, 5);
        const lastWord = props.label.slice(-5);
        return `${firstWord}..${lastWord}`;
      }
      return props.label;
  }
});

/**
 * Show tooltip the full label text when:
 * - The format is not nowrap and the length is more than 12 chars
 * - The format is username and the length is more than 8 characters
 */
const badgeTooltip = computed(() => {
  return (
    props.tooltip ||
    ((props.format !== 'nowrap' && props.label.length > 12) ||
    (props.format === 'username' && props.label.length > 8)
      ? props.label
      : undefined)
  );
});

/**
 * Function to handle keyboard events for editing text.
 * @param ev - The keyboard event.
 */
const onEditText = (ev: KeyboardEvent): void => {
  const src = ev.target as HTMLSpanElement;
  // If Enter or Tab key is pressed, prevent default action and blur the element.
  if (ev.code === 'Enter' || ev.code === 'Tab') {
    ev.preventDefault();
    src.blur();
  }
};

/**
 * Function to update the label text and emit events.
 * @param inputElement - the conteneditable span element
 * @param badgeEl - The HTML element representing the label.
 */
const updateLabel = (inputElement: HTMLElement, badgeEl: HTMLElement): void => {
  // If the new text content is different from the current label text.
  const text = inputElement.textContent;

  if (text !== props.label) {
    // Trim the text to removes extra spaces.
    const trimmed = text?.trim() ?? null;
    const trimmedOrRestored =
      props.restoreOnEmpty && !trimmed ? props.label : trimmed;
    inputElement.textContent = trimmedOrRestored;

    emit('update:label', trimmedOrRestored, props.label);

    if (badgeEl && !trimmedOrRestored && props.removable != false) {
      // Remove the badge element from the DOM if the text is empty.
      badgeEl.remove();

      emit('remove');
    }
  }
};
</script>

<template>
  <span
    ref="badge"
    v-if="hasValue"
    v-tooltip.top="{ value: badgeTooltip, autoHide: false }"
    v-bind="
      $preset.badge?.root({
        props: { severity: badgeSeverity, disabled },
      })
    "
  >
    <span
      ref="input"
      v-bind="$preset.badge?.input({ props, context: { badgeTooltip } })"
      :contenteditable="editable"
      @blur="
        updateLabel(
          $refs['input'] as HTMLSpanElement,
          $refs['badge'] as HTMLSpanElement,
        )
      "
      @keydown="onEditText"
      spellcheck="false"
    >
      {{ formattedText }}
    </span>

    <Button
      v-if="props.removable || props.disableRemoveButton"
      v-bind="
        $preset.badge?.removebutton({ props: { badgeSeverity, disabled } })
      "
      :disabled="
        props.disabled || (!props.removable && props.disableRemoveButton)
      "
      :severity="badgeSeverity"
      @click="emit('remove')"
      icon="close"
      rounded
      text
    />
  </span>

  <span v-else aria-label="invalid-label">-</span>
</template>
